/**
 * Formatting utilities.
 */

import type { Trace } from '../api/client';

/**
 * Format a duration in milliseconds to a human-readable string.
 */
export function formatDuration(ms: number | undefined | null): string {
  if (ms === undefined || ms === null) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`;
  return `${(ms / 3600000).toFixed(1)}h`;
}

/**
 * Format a token count with K/M suffix.
 */
export function formatTokens(count: number | undefined | null): string {
  if (count === undefined || count === null) return '-';
  if (count < 1000) return count.toString();
  if (count < 1000000) return `${(count / 1000).toFixed(1)}K`;
  return `${(count / 1000000).toFixed(1)}M`;
}

/**
 * Format a cost in USD.
 */
export function formatCost(amount: number | undefined | null): string {
  if (amount === undefined || amount === null) return '-';
  if (amount < 0.01) return `$${amount.toFixed(4)}`;
  return `$${amount.toFixed(2)}`;
}

/**
 * Format a byte count using binary units.
 */
export function formatBytes(bytes: number | undefined | null): string {
  if (bytes === undefined || bytes === null) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

/**
 * Format a date to relative time (e.g., "2m ago").
 */
export function formatRelativeTime(dateStr: string | undefined | null): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

/**
 * Format a timestamp as an absolute ISO string.
 */
export function formatTimestamp(dateStr: string | undefined | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toISOString();
}

/**
 * Calculate duration from start and end times.
 */
export function calculateDuration(
  startedAt: string | undefined,
  endedAt: string | undefined | null
): number | null {
  if (!startedAt) return null;
  const start = new Date(startedAt).getTime();
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  return end - start;
}

/** Whether a trace is still running, and its derived duration. */
export interface TraceTiming {
  running: boolean;
  durationMs: number | null;
}

/** Tooltip for a terminal trace whose duration is unknown. */
export const UNAVAILABLE_DURATION_TITLE = 'No end time recorded, so the duration is unavailable.';

/**
 * Status decides whether a missing end time means "still running". A RUNNING
 * trace without an end runs its duration to now; a terminal trace without an
 * end has no known duration.
 */
export function getTraceTiming(
  trace: Pick<Trace, 'status' | 'started_at' | 'ended_at'>
): TraceTiming {
  const running = trace.status === 'RUNNING' && !trace.ended_at;
  return {
    running,
    durationMs:
      running || trace.ended_at ? calculateDuration(trace.started_at, trace.ended_at) : null,
  };
}

/**
 * Formats a derived duration. Values under 10 ms show as "<10 ms" because
 * timestamp rounding makes smaller differences unreliable.
 */
export function formatDerivedDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) {
    return '—';
  }
  if (ms < 10) {
    return '<10 ms';
  }
  if (ms < 1000) {
    return `${Math.round(ms)} ms`;
  }
  if (ms < 60_000) {
    return `${(ms / 1000).toFixed(2)}s`;
  }
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes}m ${seconds}s`;
}

/**
 * Format an exact local time, e.g. "Aug 17, 03:02". Lists show exact times
 * instead of relative ones so two rows can be compared.
 */
export function formatExactTime(dateStr: string | undefined | null): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}
